import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IssueDataGrid = ({ issues, selectedIssue, onSelectIssue, onEditIssue, onBulkAction }) => {
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'createdDate', direction: 'desc' });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'in-progress':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'pending-approval':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'closed':
        return 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedIssues(issues?.map(issue => issue?.id));
    } else {
      setSelectedIssues([]);
    }
  };

  const handleSelectIssue = (issueId) => {
    setSelectedIssues(prev =>
      prev?.includes(issueId)
        ? prev?.filter(id => id !== issueId)
        : [...prev, issueId]
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isOverdue = (targetDate) => {
    return new Date(targetDate) < new Date();
  };

  return (
    <div className="h-full flex flex-col bg-card">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-foreground">
            Issues & Risks ({issues?.length})
          </h2>
          {selectedIssues?.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {selectedIssues?.length} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedIssues?.length > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                iconName="Users"
                onClick={() => onBulkAction('assign')}
              >
                Assign
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Tag"
                onClick={() => onBulkAction('categorize')}
              >
                Categorize
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Bell"
                onClick={() => onBulkAction('notify')}
              >
                Notify
              </Button>
            </>
          )}
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => onEditIssue(null)}
          >
            New Issue
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-auto custom-scrollbar">
        <table className="w-full">
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr>
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedIssues?.length === issues?.length}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70" onClick={() => handleSort('id')}>
                <div className="flex items-center gap-2">
                  ID
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70" onClick={() => handleSort('title')}>
                <div className="flex items-center gap-2">
                  Title
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Shipment
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70" onClick={() => handleSort('severity')}>
                <div className="flex items-center gap-2">
                  Severity
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Owner
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70" onClick={() => handleSort('createdDate')}>
                <div className="flex items-center gap-2">
                  Created
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Target Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {issues?.map((issue) => (
              <tr
                key={issue?.id}
                className={`hover:bg-muted/50 transition-colors cursor-pointer ${
                  selectedIssue?.id === issue?.id ? 'bg-primary/5' : ''
                }`}
                onClick={() => onSelectIssue(issue)}
              >
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIssues?.includes(issue?.id)}
                    onChange={() => handleSelectIssue(issue?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground">{issue?.id}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-start gap-2">
                    <span className="text-sm text-foreground font-medium">{issue?.title}</span>
                    {issue?.hasAttachments && (
                      <Icon name="Paperclip" size={14} className="text-muted-foreground mt-0.5" />
                    )}
                    {issue?.escalated && (
                      <Icon name="AlertTriangle" size={14} className="text-warning mt-0.5" />
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">{issue?.shipmentRef}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(issue?.severity)}`}>
                    {issue?.severity?.charAt(0)?.toUpperCase() + issue?.severity?.slice(1)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">{issue?.category}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{issue?.owner}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(issue?.status)}`}>
                    {issue?.status?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ')}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">{formatDate(issue?.createdDate)}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${isOverdue(issue?.targetDate) && issue?.status !== 'resolved' && issue?.status !== 'closed' ? 'text-error font-medium' : 'text-muted-foreground'}`}>
                      {formatDate(issue?.targetDate)}
                    </span>
                    {isOverdue(issue?.targetDate) && issue?.status !== 'resolved' && issue?.status !== 'closed' && (
                      <Icon name="AlertCircle" size={14} className="text-error" />
                    )}
                  </div>
                </td>
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <button
                    onClick={() => onEditIssue(issue)}
                    className="p-1 rounded hover:bg-muted transition-colors"
                    aria-label="Edit issue"
                  >
                    <Icon name="Edit2" size={16} className="text-muted-foreground" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IssueDataGrid;